import React, {useState} from 'react'
import {Box, Grid, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PDBottomPLPLayout} from '../../../page-designer/layouts'
import {getEnvStaleTime} from '../../../utils/utils'
import {usePage} from '@salesforce/commerce-sdk-react'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import FaqList from './faq-list'
import {Hide, Show} from '@chakra-ui/react'
import SiblingCategories from './sibling-categories'
import PropTypes from 'prop-types'

const PAGEDESIGNER_TO_COMPONENT = {
    'commerce_layouts.pdBottomPLPLayout': PDBottomPLPLayout
}

/**
 * BottomElements is a React functional component that renders the bottom section of a product list page.
 * It utilizes various UI components and context to display content such as FAQ, sibling categories,
 * and a bottom description based on the provided category data.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.category - The category object containing data for rendering the bottom elements.
 * @param {string} [props.category.c_bottomPLPLayoutID] - The ID of the bottom PLP layout to be used.
 * @param {string} [props.category.c_bottomDescription] - The description text to be displayed at the bottom.
 * @param {Array} [props.category.c_rndSiblingCategories] - An array of sibling categories to be displayed.
 *
 * @returns {JSX.Element} The rendered bottom elements section.
 *
 * @todo Implement the PDC-HOME-PATTERN-FINDER-PUSH functionality after go live.
 */
const BottomElements = ({category = {}}) => {
    // Bottom content
    const [contextValuePLPBottom] = useState({components: PAGEDESIGNER_TO_COMPONENT})
    const bottomPLPLayoutID = category?.c_bottomPLPLayoutID || ''
    const staleTime = getEnvStaleTime()
    const {data: bottomPLPPage} = usePage(
        {parameters: {pageId: 'bottom-plp-page'}},
        {staleTime: staleTime}
    )
    const bottomPLPLayoutComponent = bottomPLPPage?.regions?.[0].components?.find(
        (comp) => comp?.data?.bottomPLPVariantID === bottomPLPLayoutID
    )
    const bottomDescription = category?.c_bottomDescription && (
        <Text textStyle="bodyLarge2" color="darkGray">
            {category?.c_bottomDescription}
        </Text>
    )
    const siblingCategories = category?.c_rndSiblingCategories

    return (
        <Box as="section">
            {/* @TODO: PDC-HOME-PATTERN-FINDER-PUSH: to implement AFTER go live */}
            {bottomPLPLayoutComponent && (
                <PageContext.Provider value={contextValuePLPBottom}>
                    <Component component={bottomPLPLayoutComponent} />
                </PageContext.Provider>
            )}

            <Stack as="section" px={[10, 10, 20, 20]} py={[45, 45, 45, 70]} gap={[45, 45, 45, 35]}>
                {/* FAQ section */}
                <FaqList />

                {/* Sister categories & bottom description */}
                <Grid templateColumns={{base: '1fr', lg: '1fr 1fr'}} gap={{base: 45, lg: 60}}>
                    <Hide above="lg">{bottomDescription}</Hide>

                    <Box display={siblingCategories ? 'block' : {base: 'none', lg: 'block'}}>
                        {siblingCategories && <SiblingCategories categories={siblingCategories} />}
                    </Box>

                    <Show above="lg">{bottomDescription}</Show>
                </Grid>
            </Stack>
        </Box>
    )
}

BottomElements.propTypes = {
    category: PropTypes.object
}

export default BottomElements
